import { useCallback, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { assistantApi, type AssistantResponse, type ToolInvocation } from './assistantApi';
import { usePageEntityContext } from '@/lib/pageEntityContext';
import { getErrorMessage, isNetworkError, isTimeoutError } from '@/lib/apiClient';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  toolsUsed?: ToolInvocation[];
  timestamp: Date;
  isLoading?: boolean;
}

export function useAssistant() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const context = usePageEntityContext();
  const messageIdCounter = useRef(0);

  const nextId = () => `msg-${++messageIdCounter.current}`;

  const mutation = useMutation({
    mutationFn: async (message: string) => {
      return assistantApi.askInContext({
        message,
        conversationId,
        entityType: context.entityType,
        entityId: context.entityId,
        module: context.module,
      });
    },
    onMutate: (message: string) => {
      // Add user message immediately
      const userMsg: ChatMessage = {
        id: nextId(),
        role: 'user',
        content: message,
        timestamp: new Date(),
      };
      // Add loading placeholder for assistant
      const loadingMsg: ChatMessage = {
        id: nextId(),
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isLoading: true,
      };
      setMessages((prev) => [...prev, userMsg, loadingMsg]);
    },
    onSuccess: (response: AssistantResponse) => {
      setConversationId(response.conversationId);
      // Replace loading message with actual response
      setMessages((prev) => {
        const withoutLoading = prev.filter((m) => !m.isLoading);
        return [
          ...withoutLoading,
          {
            id: nextId(),
            role: 'assistant' as const,
            content: response.text,
            toolsUsed: response.toolsUsed,
            timestamp: new Date(),
          },
        ];
      });
    },
    onError: (error: unknown) => {
      // getErrorMessage's network/timeout texts are form-save wording
      // ("try saving again") — phrase chat failures as chat failures.
      const errorMessage = isTimeoutError(error)
        ? 'The assistant took too long to respond. Try asking again — simpler questions answer faster.'
        : isNetworkError(error)
          ? 'The assistant could not be reached. Check that the backend is running, then try again.'
          : getErrorMessage(error) || 'An error occurred while processing your request.';
      // Replace loading message with error
      setMessages((prev) => {
        const withoutLoading = prev.filter((m) => !m.isLoading);
        return [
          ...withoutLoading,
          {
            id: nextId(),
            role: 'assistant' as const,
            content: `Error: ${errorMessage}`,
            timestamp: new Date(),
          },
        ];
      });
    },
  });

  const send = useCallback(
    (message: string) => {
      if (!message.trim() || mutation.isPending) return;
      mutation.mutate(message.trim());
    },
    [mutation],
  );

  const clearChat = useCallback(() => {
    setMessages([]);
    setConversationId(undefined);
  }, []);

  return {
    messages,
    send,
    clearChat,
    isLoading: mutation.isPending,
    context,
  };
}
